import type { TFunction } from 'i18next';

import { formatEventTimestamp } from '@klareospace/pharos-lib-front-utils';
import {
  HISTORY_VALUE_KIND,
  ORDER_HISTORY_EVENT_CODE,
  type HistoryEntry,
  type HistoryListDiffValue,
  type HistoryValue,
} from '@klareospace/pharos-lib-types';
import type { HistoryFeedEntry } from '@klareospace/pharos-lib-web-utils';

import { translateStatusReason } from './status-reason';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MapHistoryEntriesOptions {
  /** Translates `history.events.<code>` and status-reason codes (`orders` namespace `t`). */
  readonly t: (key: string) => string;
  /**
   * Translates the "by {{name}}" actor line and the system/unknown-actor fallback labels (`common`
   * namespace `t`). Typed as `TFunction` (not a bare `(key: string) => string`) since the actor
   * line needs interpolation support.
   */
  readonly tCommon: TFunction;
}

// ─── Mapping ──────────────────────────────────────────────────────────────────

/** Maps a paginated `HistoryEntry[]` (already most-recent-first) to `HistoryFeed`-ready entries. */
export function mapHistoryEntries(entries: HistoryEntry[], options: MapHistoryEntriesOptions): HistoryFeedEntry[] {
  return entries.map((entry, index) => mapHistoryEntry(entry, index, options));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveHistoryEventLabel(code: string, options: MapHistoryEntriesOptions): string {
  if (code === ORDER_HISTORY_EVENT_CODE.STATUS) return options.tCommon('fields.statusChanged');
  return options.t(`history.events.${code}`);
}

function mapHistoryEntry(entry: HistoryEntry, index: number, options: MapHistoryEntriesOptions): HistoryFeedEntry {
  return {
    actorLabel: resolveActorLabel(entry, options.tCommon),
    id: `${entry.code}-${entry.timestamp}-${index}`,
    label: resolveHistoryEventLabel(entry.code, options),
    newValue: formatValue(entry.code, entry.newValue, options),
    previousValue: formatValue(entry.code, entry.previousValue, options),
    timestamp: formatEventTimestamp(entry.timestamp),
  };
}

/**
 * Composes the shared "by {name}" actor line — name resolves to the actor's name, the translated
 * System label, or the unknown-actor fallback. Shared by both the orders and tasks history
 * mappers so both history sections render the identical actor line.
 */
export function resolveActorLabel(entry: HistoryEntry, tCommon: TFunction): string {
  const name = resolveActorName(entry, tCommon);
  return tCommon('history.by', { name });
}

function resolveActorName(entry: HistoryEntry, tCommon: TFunction): string {
  if (entry.actor.kind === 'system') return tCommon('history.system');
  return entry.actor.name ?? tCommon('history.unknownActor');
}

/** Applies the per-code formatting `HistoryFeed`'s formatter props can't express: status-reason translation and listDiff item descriptions. Every other kind/code passes through unchanged. */
function formatValue(code: string, value: HistoryValue | null, options: MapHistoryEntriesOptions): HistoryValue | null {
  if (value === null) return null;
  if (value.kind === HISTORY_VALUE_KIND.STATUS) {
    return { ...value, reason: translateStatusReason(options.t, value.reason) };
  }
  if (value.kind === HISTORY_VALUE_KIND.LIST_DIFF) {
    return formatListDiffValue(code, value);
  }
  return value;
}

// ─── listDiff item formatting (handlingUnits / items) ──────────────────────────

function formatListDiffValue(code: string, value: HistoryListDiffValue): HistoryValue {
  const formatItem = resolveListDiffItemFormatter(code);
  if (formatItem === null) return value;
  return {
    added: value.added.map(formatItem),
    kind: HISTORY_VALUE_KIND.LIST_DIFF,
    removed: value.removed.map(formatItem),
    updated: value.updated.map(formatItem),
  };
}

function resolveListDiffItemFormatter(code: string): ((item: unknown) => unknown) | null {
  if (code === ORDER_HISTORY_EVENT_CODE.HANDLING_UNITS) return formatHandlingUnitDiffItem;
  if (code === ORDER_HISTORY_EVENT_CODE.ITEMS) return formatItemDiffItem;
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Reads `code.value` off a raw diff item (shaped like the shared `Barcode` interface), if present. */
function extractBarcodeValue(item: Record<string, unknown>): string | undefined {
  const code = item['code'];
  if (!isRecord(code)) return undefined;
  return typeof code['value'] === 'string' ? code['value'] : undefined;
}

/** Appends a `×{quantity}` suffix whenever the item's quantity is a number. */
function appendQuantitySuffix(label: string, item: Record<string, unknown>): string {
  const quantity = item['quantity'];
  return typeof quantity === 'number' ? `${label} ×${quantity}` : label;
}

/**
 * `HandlingUnit`-shaped item → `type`, falling back to barcode `code.value`. Matches the backend's
 * `handlingUnits` diff identity (`diffKey: 'type'`). Malformed items pass through unchanged.
 */
function formatHandlingUnitDiffItem(item: unknown): unknown {
  if (!isRecord(item)) return item;
  const type = typeof item['type'] === 'string' ? item['type'] : undefined;
  const label = type ?? extractBarcodeValue(item);
  if (label === undefined) return item;
  return appendQuantitySuffix(label, item);
}

/** `Item`-shaped item → `label`, falling back to `code.value`. Malformed items pass through unchanged. */
function formatItemDiffItem(item: unknown): unknown {
  if (!isRecord(item)) return item;
  const label = typeof item['label'] === 'string' ? item['label'] : extractBarcodeValue(item);
  if (label === undefined) return item;
  return appendQuantitySuffix(label, item);
}
